import React, { useState } from 'react';
import { 
  Layout, 
  Form, 
  Input, 
  InputNumber,
  Button, 
  Upload,
  Alert, 
  Typography,
  Row,
  Col,
  Grid,
  Space,
  message,
  Card
} from 'antd';
import { 
  UserOutlined,
  UploadOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useClientAdmin } from '../../context/ClientAdminContext';
import { API_ORIGIN } from '../../config';

const { Content, Header } = Layout;
const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const Dfm2026 = () => {
  const navigate = useNavigate();
  const { clientAdmin } = useClientAdmin();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [motivationWordCount, setMotivationWordCount] = useState(0);
  const [bioWordCount, setBioWordCount] = useState(0);
  const [hasBothCvAndBio, setHasBothCvAndBio] = useState(false);

  const screens = Grid.useBreakpoint();
  const isSmall = !screens.md;

  const formFontColour = '#FFAC00'; // Rock'n Malta color scheme
  const maxBioWords = 150;

  const formId = 9; // DFM2026 form ID - adjust as needed
  
  // Get specific activity status
  const [isFormActive, setIsFormActive] = useState(true); // Default to true to show form initially

  // Fetch form activity status on component mount
  React.useEffect(() => {
    const fetchFormStatus = async () => {
      try {
        const response = await fetch(`${API_ORIGIN}/api/clientadmin/specificForm?id=${formId}`, {
          method: 'GET',
        });
        const data = await response.json();
        
        if (response.ok && data.success) {
          // Backend returns 0 or 1 (tinyint) - convert to boolean
          const isActive = data.activeForm === 1 || data.isActive === 1;
          setIsFormActive(isActive);
        } else {
          setIsFormActive(false); // Disable form if fetch fails
        }
      } catch (error) {
        setIsFormActive(false); // Disable form on error
      }
    };

    fetchFormStatus();
  }, []);

  // Handle word count for bio field (max 150 words)
  const handleBioChange = (e) => {
    const text = (e && e.target && e.target.value) ? e.target.value : '';
    const words = text.match(/\b\w+\b/g) || [];
    const currentWordCount = words.length;

    if (currentWordCount > maxBioWords) {
      const trimmedText = words.slice(0, maxBioWords).join(' ');
      form.setFieldsValue({ cvBio: trimmedText });
      setBioWordCount(maxBioWords);
      message.warning(`Bio is limited to ${maxBioWords} words`);
    } else {
      form.setFieldsValue({ cvBio: text });
      setBioWordCount(currentWordCount);
    }

    // Check if CV is also uploaded
    const cv = form.getFieldValue('cv');
    if (text.trim() && cv?.length > 0) {
      setHasBothCvAndBio(true);
      message.warning('Please choose either CV upload OR bio text, not both');
    } else {
      setHasBothCvAndBio(false);
    }
  };

  // Handle word count for motivation field (brief paragraph - no strict limit but track it)
  const handleMotivationChange = (e) => {
    const text = (e && e.target && e.target.value) ? e.target.value : '';
    const words = text.match(/\b\w+\b/g) || [];
    const currentWordCount = words.length;
    setMotivationWordCount(currentWordCount);
  };

  // Handle CV upload change
  const handleCvChange = (info) => {
    const cvBio = form.getFieldValue('cvBio');
    if (info.fileList.length > 0 && cvBio?.trim()) {
      setHasBothCvAndBio(true);
      message.warning('Please choose either CV upload OR bio text, not both');
    } else {
      setHasBothCvAndBio(false);
    }
  };

  // File upload props for CV/Bio (PDF, max 5MB)
  const cvUploadProps = {
    beforeUpload: (file) => {
      const isPdf = file.type === 'application/pdf';
      if (!isPdf) {
        message.error('You can only upload PDF files!');
        return false;
      }
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error('File must be smaller than 5MB!');
        return false;
      }
      return false; // Prevent auto upload
    },
    multiple: false,
    maxCount: 1,
  };

  // File upload props for Video (ZIP file only, max 500MB, max 3 min video)
  const videoUploadProps = {
    accept: '.zip,application/zip,application/x-zip-compressed',
    beforeUpload: (file) => {
      const isZip = file.type === 'application/zip' || file.type === 'application/x-zip-compressed' || file.name.endsWith('.zip');
      if (!isZip) {
        message.error('You can only upload ZIP files!');
        return false;
      }
      const isLt500M = file.size / 1024 / 1024 < 500;
      if (!isLt500M) {
        message.error('Video ZIP file must be smaller than 500MB!');
        return false;
      }
      return false; // Prevent auto upload
    },
    multiple: false,
    maxCount: 1,
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const formData = new FormData();
      
      // Add text fields
      formData.append('name', values.name);
      formData.append('surname', values.surname);
      formData.append('age', values.age);
      formData.append('email', values.email);
      formData.append('contactNumber', values.contactNumber);
      formData.append('motivation', values.motivation);
      
      // Add CV or Bio (either file or text)
      if (values.cv && values.cv.length > 0) {
        formData.append('cv', values.cv[0].originFileObj);
      } else if (values.cvBio) {
        formData.append('cvBio', values.cvBio);
      }
      
      // Add video ZIP file
      if (values.video && values.video.length > 0) {
        formData.append('video', values.video[0].originFileObj);
      }

      // Debug: log payload

      const response = await fetch(`${API_ORIGIN}/api/dfm2026/submit`, {
        method: 'POST',
        body: formData,
      });

      // Try to parse JSON, but handle non-JSON responses
      let data;
      try {
        data = await response.json();
      } catch (err) {
        const text = await response.text();
        data = { success: false, error: text };
      }

      if (response.ok && data.success) {
        setSuccess(true);
        message.success('Application submitted successfully!');
        form.resetFields();
        setBioWordCount(0);
        setMotivationWordCount(0);
        window.scrollTo(0, 0);
      } else {
        const errMsg = data.error || `Submission failed (status ${response.status})`;
        setError(errMsg);
        message.error(errMsg);
      }
    } catch (error) {
      setError('An error occurred while submitting the application. Please try again.');
      message.error('An error occurred while submitting the application');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{`
        /* Smooth transitions for buttons */
        .ant-btn {
          transition: transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease, border-color 180ms ease, color 180ms ease;
        }

        /* Hover colour for all non-primary buttons */
        .ant-btn:not(.dfm2026-primary):hover {
          background-color: ${formFontColour} !important;
          border-color: ${formFontColour} !important;
          color: #fff !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 8px 20px rgba(255,172,0,0.12) !important;
        }

        /* Primary submit button base style */
        .dfm2026-primary {
          background-color: ${formFontColour} !important;
          border-color: ${formFontColour} !important;
          color: #fff !important;
          transform: translateY(0);
          box-shadow: 0 6px 18px rgba(24,144,255,0.08);
        }

        /* Primary submit hover animation */
        .dfm2026-primary:hover {
          transform: translateY(-4px) !important;
          box-shadow: 0 14px 36px rgba(255,172,0,0.22) !important;
        }

        .dfm2026-primary:active {
          transform: translateY(-1px) !important;
        }
      `}</style>
      <Layout style={{ minHeight: '100vh', background: '#fafafa', overflowX: 'hidden' }}>
      <Header style={{ 
        padding: '12px 16px', 
        background: '#fff', 
        borderBottom: '1px solid #f0f0f0',
        height: 'auto',
        overflowX: 'hidden'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          alignItems: 'center', 
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div style={{ 
            flex: '0 0 auto',
            display: 'flex', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            paddingRight: isSmall ? 8 : 0
          }}>
            <img
              src="/images/FM_Logo.png"
              alt="Festivals Malta"
              onClick={() => navigate('/')}
              style={{
                height: isSmall ? '32px' : '36px',
                width: 'auto',
                maxWidth: '160px',
                cursor: 'pointer',
                objectFit: 'contain',
              }}
            />
          </div>
        </div>
      </Header>

      <Content style={{ padding: 'clamp(20px, 5vw, 48px)', overflowX: 'hidden' }}>
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: isSmall ? '0 12px' : '0 20px', width: '100%', boxSizing: 'border-box' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(24px, 6vw, 48px)' }}>
             <img
              src="/images/DFM_Logo.png"
              alt="Dance Festival Malta"
              style={{
                height: isSmall ? '100px' : 'clamp(140px, 22vw, 220px)',
                maxWidth: '100%',
                maxHeight: '240px',
                objectFit: 'contain'
              }}
            />
            <Title level={2} style={{ color: '#222', letterSpacing: '0.5px', marginBottom: '8px', fontSize: 'clamp(18px, 4vw, 24px)', marginTop: '24px' }}>
              Community / Artivism Performance
            </Title>
            <Title level={1} style={{ color: formFontColour, marginBottom: '8px', fontSize: 'clamp(26px, 6vw, 40px)', fontWeight: 'bold' }}>
              ART UNDER THREAT
            </Title>
            <Paragraph style={{ color: '#555', fontSize: 'clamp(16px, 4vw, 20px)', marginBottom: '24px', fontWeight: '500' }}>
              Dance Festival Malta 2026
            </Paragraph>
            
            {/* Hero Image */}
            <div style={{ marginBottom: '24px' }}>
              <img
                src="/images/DFM2026/heroimage.jpg"
                alt="Art Under Threat Performance"
                style={{
                  width: '100%',
                  maxWidth: '800px',
                  height: 'auto',
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                }}
              />
              <Paragraph style={{ color: '#888', fontSize: '12px', fontStyle: 'italic', marginTop: '8px' }}>
                Photo by Elisa von Brockdorff
              </Paragraph>
            </div>
          </div>

          {success && (
            <Alert
              message="Application submitted successfully!"
              description="A representative will get back to you once it is vetted."
              type="success"
              style={{ marginBottom: '24px' }}
              closable
              onClose={() => setSuccess(false)}
            />
          )}

          {error && (
            <Alert
              message={error}
              type="error"
              style={{ marginBottom: '24px' }}
              closable
              onClose={() => setError('')}
            />
          )}

          <Card 
            style={{ 
              borderRadius: '12px', 
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: '1px solid #f0f0f0',
              maxWidth: '100%',
              overflowX: 'hidden'
            }}
            styles={{ body: { padding: isSmall ? 20 : 40 } }}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              requiredMark={false}
              style={{ maxWidth: '100%', overflowX: 'hidden' }}
              disabled={!isFormActive}
            >
              <Row gutter={isSmall ? 12 : 24} style={{ maxWidth: '100%' }}>

              {/* Personal Information Section */}
              <Col xs={24}>
                <Title level={4} style={{ color: formFontColour, marginBottom: 16 }}>
                  <UserOutlined /> Personal Information
                </Title>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Name</span>}
                  name="name"
                  rules={[{ required: true, message: 'Please enter your name' }]}
                >
                  <Input placeholder="Enter your name" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Surname</span>}
                  name="surname"
                  rules={[{ required: true, message: 'Please enter your surname' }]}
                >
                  <Input placeholder="Enter your surname" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Age</span>}
                  name="age"
                  rules={[
                    { required: true, message: 'Please enter your age' },
                    { type: 'number', min: 1, max: 120, message: 'Please enter a valid age' }
                  ]}
                >
                  <InputNumber placeholder="Enter your age" style={{ width: '100%' }} />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Contact Number</span>}
                  name="contactNumber"
                  rules={[{ required: true, message: 'Please enter your contact number' }]}
                >
                  <Input placeholder="Enter your contact number" />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Email Address</span>}
                  name="email"
                  rules={[
                    { required: true, message: 'Please enter your email' },
                    { type: 'email', message: 'Please enter a valid email' }
                  ]}
                >
                  <Input placeholder="Enter your email address" />
                </Form.Item>
              </Col>

              {/* CV or Bio Section */}
              <Col xs={24}>
                <Title level={4} style={{ color: formFontColour, marginBottom: 16, marginTop: 16 }}>
                  CV or Bio
                </Title>
                <Paragraph style={{ color: '#666', marginBottom: 16 }}>
                  You can either upload a CV (1 page PDF) or write a bio below (max 150 words)
                </Paragraph>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Upload CV (1 page PDF)</span>}
                  name="cv"
                  valuePropName="fileList"
                  getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                  rules={[
                    {
                      validator: async (_, value) => {
                        const cvBio = form.getFieldValue('cvBio');
                        if (!value?.length && !cvBio) {
                          return Promise.reject(new Error('Please either upload a CV or write a bio'));
                        }
                        if (value?.length && cvBio?.trim()) {
                          return Promise.reject(new Error('Please choose only ONE: either CV upload or bio text'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <Upload {...cvUploadProps} onChange={handleCvChange}>
                    <Button icon={<UploadOutlined />} style={{ width: isSmall ? '100%' : undefined }}>
                      Upload CV (Max 5MB)
                    </Button>
                  </Upload>
                </Form.Item>
              </Col>

              {hasBothCvAndBio && (
                <Col xs={24}>
                  <Alert
                    message="Please choose only one option"
                    description="You can either upload a CV OR write a bio, not both. Please remove one to continue."
                    type="warning"
                    showIcon
                    closable
                    onClose={() => setHasBothCvAndBio(false)}
                    style={{ marginBottom: 16 }}
                  />
                </Col>
              )}

              <Col xs={24}>
                <Paragraph style={{ textAlign: 'center', color: '#999', margin: '8px 0' }}>
                  — OR —
                </Paragraph>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Bio (Max 150 words)</span>}
                  name="cvBio"
                  rules={[
                    {
                      validator: async (_, value) => {
                        const cv = form.getFieldValue('cv');
                        if (!value && !cv?.length) {
                          return Promise.reject(new Error('Please either upload a CV or write a bio'));
                        }
                        if (value?.trim() && cv?.length) {
                          return Promise.reject(new Error('Please choose only ONE: either CV upload or bio text'));
                        }
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <div>
                    <TextArea 
                      rows={4} 
                      placeholder="Write your bio here (max 150 words)"
                      onChange={handleBioChange}
                    />
                    <div style={{ 
                      textAlign: 'right', 
                      marginTop: '4px', 
                      fontSize: '12px',
                      color: bioWordCount > maxBioWords ? '#ff4d4f' : '#888'
                    }}>
                      {bioWordCount}/{maxBioWords} words
                    </div>
                  </div>
                </Form.Item>
              </Col>

              {/* Video Section */}
              <Col xs={24}>
                <Title level={4} style={{ color: formFontColour, marginBottom: 16, marginTop: 16 }}>
                  Performance Video
                </Title>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Upload Video (Max 3 minutes showcasing your skills and interests as a performer)</span>}
                  name="video"
                  valuePropName="fileList"
                  getValueFromEvent={(e) => (Array.isArray(e) ? e : e?.fileList)}
                  rules={[{ required: true, message: 'Please upload a video showcasing your performance skills' }]}
                >
                  <Upload {...videoUploadProps}>
                    <Button icon={<UploadOutlined />} style={{ width: isSmall ? '100%' : undefined }}>
                      Upload Performance Video ZIP (Max 500MB)
                    </Button>
                  </Upload>
                </Form.Item>
                <Paragraph style={{ color: '#666', fontSize: '12px', marginTop: '-8px' }}>
                  Please upload a ZIP file containing your video (max 3 minutes) showcasing your skills and interests as a performer
                </Paragraph>
              </Col>

              {/* Motivation Section */}
              <Col xs={24}>
                <Title level={4} style={{ color: formFontColour, marginBottom: 16, marginTop: 16 }}>
                  Motivation
                </Title>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={<span style={{ color: '#000' }}>Why do you want to participate?</span>}
                  name="motivation"
                  rules={[{ required: true, message: 'Please explain why you want to participate' }]}
                >
                  <div>
                    <TextArea 
                      rows={4} 
                      placeholder="Write a brief paragraph explaining why you want to participate in Art Under Threat"
                      onChange={handleMotivationChange}
                    />
                    <div style={{ 
                      textAlign: 'right', 
                      marginTop: '4px', 
                      fontSize: '12px',
                      color: '#888'
                    }}>
                      {motivationWordCount} words
                    </div>
                  </div>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item>
                  <Button 
                    className="dfm2026-primary"
                    type="primary" 
                    htmlType="submit" 
                    loading={loading}
                    size="large"
                    block
                    style={{
                      width: '100%', 
                      height: '56px',
                      fontSize: '16px',
                      fontWeight: '600',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(24, 144, 255, 0.12)'
                    }}
                  >
                    Submit Application
                  </Button>
                </Form.Item>
              </Col>
              </Row>
            </Form>
          </Card>
        </div>
      </Content>
    </Layout>
    </>
  );
};

export default Dfm2026;
